import turf from '@turf/turf';
import { map, filter, findIndex, forEach, get, isEqual, isNaN, matches, replace } from 'lodash';
import h3 from 'h3-js';
import { Bert } from 'meteor/themeteorchef:bert';

import { GEOREF_TYPES } from '../api/Layers/Layers';

/**
 * Transform the table into a geojson.
 *
 * @param {object} arg - The parameters object.
 * @param {object} arg.georefType - The type of georeferencing.
 * @param {object} arg.h3Index - The name of the attribute to use as H3 index.
 * @param {object} arg.spatialTableFields - The keys that contain the longitud and latitud for the Spatial Table layers.
 * @param {Array} arg.tableBody - The rest of the table.
 * @param {Array} arg.tableTitle - The first row keys of the table.
 * @returns {object} The geojson.
 * @throws {Error} If something goes wrong.
 */
const getGeoJSONFromTable = ({
  georefType,
  h3Index,
  spatialTableFields,
  tableBody,
  tableTitle
}) => {
  let geojson = {};
  const longitudeIndex = findIndex(tableTitle, matches(get(spatialTableFields, 'longitude', '')));
  const latitudeIndex = findIndex(tableTitle, matches(get(spatialTableFields, 'latitude', '')));
  const h3IndexIndex = findIndex(tableTitle, matches(h3Index));

  const validBodyRows = filter(tableBody, bodyRow => {
    if (GEOREF_TYPES.LONGLAT === georefType) {
      const longitude = Number(replace(bodyRow[longitudeIndex], ',', '.'));
      const latitude = Number(replace(bodyRow[latitudeIndex], ',', '.'));
      return isEqual(bodyRow.length, tableTitle.length) && !isNaN(longitude) && !isNaN(latitude);
    }
    return GEOREF_TYPES.H3 === georefType;
  });

  geojson = turf.featureCollection(
    map(validBodyRows, bodyRow => {
      const properties = {};
      forEach(bodyRow, (element, columnIndex) => {
        properties[`${tableTitle[columnIndex]}`] = element;
      });
      let feature = {};
      if (GEOREF_TYPES.LONGLAT === georefType) {
        const longitude = Number(replace(bodyRow[longitudeIndex], ',', '.'));
        const latitude = Number(replace(bodyRow[latitudeIndex], ',', '.'));
        feature = turf.point([longitude, latitude], properties);
      }
      if (GEOREF_TYPES.H3 === georefType)
        feature = turf.polygon([h3.h3ToGeoBoundary(bodyRow[h3IndexIndex], true)], properties);
      return feature;
    })
  );

  if (!isEqual(validBodyRows.length, tableBody.length))
    Bert.alert('Not valid rows present', 'warning');

  return geojson;
};

export default getGeoJSONFromTable;
